import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import type { ReportData } from '@/hooks/useReportActions';
import { createSOC2HeaderHTML, createSOC2ControlHTML, createSOC2OrganizationalHTML } from './soc2_pdf_helpers';

const sortVulnerabilitiesBySeverity = (vulnerabilities: ReportData['vulnerabilities']) => {
  const severityOrder = { critical: 0, high: 1, medium: 2, low: 3, info: 4, informational: 4 }
  return [...vulnerabilities].sort((a, b) => {
    const orderA = severityOrder[a.severity as keyof typeof severityOrder] ?? 5
    const orderB = severityOrder[b.severity as keyof typeof severityOrder] ?? 5
    return orderA - orderB
  })
}

// Helper function to escape HTML to prevent XSS
const escapeHTML = (str: string): string => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
}

export const generateReportPDF = async (reportData: ReportData, branding?: any): Promise<void> => {
  try {
    // Handle both new API structure and legacy structure
    const brandingData = branding?.data || branding;

    // Convert logo to base64 if it exists
    let logoBase64: string | null = null;
    if (brandingData?.branding_assets?.logo_filename) {
      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || '';
        const logoUrl = `${API_BASE_URL}/api/agency-branding/assets/${brandingData.branding_assets.logo_filename}`;
        const response = await fetch(logoUrl, { credentials: 'include' });
        const blob = await response.blob();
        logoBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Failed to load logo for PDF:', error);
      }
    }

    // Sort vulnerabilities by severity
    const sortedReportData = {
      ...reportData,
      vulnerabilities: sortVulnerabilitiesBySeverity(reportData.vulnerabilities)
    }

    // Create PDF with better page break handling
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 190; // A4 width minus margins
    const pageHeight = 267; // A4 height minus margins
    const margin = 10;
    let currentY = margin;

    // Helper function to add new page if needed
    const checkPageBreak = (contentHeight: number) => {
      if (currentY + contentHeight > pageHeight) {
        pdf.addPage();
        currentY = margin;
      }
    };

    // Helper function to render HTML to canvas and add to PDF
    const addContentToPDF = async (htmlContent: string, maxWidth = pageWidth, spacing = 5, forceNewPageIfTall = false) => {
      const tempDiv = document.createElement('div');
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.width = `${maxWidth * 3.78}px`; // Convert mm to px (96 DPI)
      tempDiv.style.backgroundColor = 'white';
      tempDiv.style.fontFamily = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif';
      tempDiv.style.lineHeight = '1.5';
      tempDiv.style.boxSizing = 'border-box';
      tempDiv.style.padding = '0';
      tempDiv.style.margin = '0';
      tempDiv.innerHTML = htmlContent;

      document.body.appendChild(tempDiv);

      // Wait for fonts to load
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(tempDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: 'white',
        logging: false,
        height: tempDiv.scrollHeight,
        width: tempDiv.scrollWidth,
        scrollY: 0,
        scrollX: 0,
        windowWidth: tempDiv.scrollWidth,
        windowHeight: tempDiv.scrollHeight,
      });

      document.body.removeChild(tempDiv);

      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      const imgWidth = maxWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // For tall content (like vulnerabilities), check if we should start a new page
      if (forceNewPageIfTall) {
        const remainingSpace = pageHeight - currentY;
        const maxContentHeight = pageHeight - margin * 2; // Max height per page

        // If content is taller than a single page, we need to split it
        if (imgHeight > maxContentHeight) {
          // Start on a fresh page to avoid awkward splits
          pdf.addPage();
          currentY = margin;

          // Split the content across multiple pages
          let remainingHeight = imgHeight;
          let sourceY = 0;

          while (remainingHeight > 0) {
            // Calculate how much we can fit on this page
            const availableHeight = pageHeight - currentY - margin; // Leave margin at bottom
            const heightToRender = Math.min(remainingHeight, availableHeight);

            // Calculate the portion of the canvas to render
            const sourceHeight = (heightToRender / imgHeight) * canvas.height;

            // Create a temporary canvas for this slice
            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = canvas.width;
            sliceCanvas.height = sourceHeight;
            const ctx = sliceCanvas.getContext('2d');

            if (ctx) {
              // Draw the slice from the original canvas
              ctx.drawImage(
                canvas,
                0, sourceY,                    // Source x, y
                canvas.width, sourceHeight,    // Source width, height
                0, 0,                          // Dest x, y
                canvas.width, sourceHeight     // Dest width, height
              );

              const sliceImgData = sliceCanvas.toDataURL('image/jpeg', 0.9);
              pdf.addImage(sliceImgData, 'PNG', margin, currentY, imgWidth, heightToRender);
            }

            // Move to next section
            sourceY += sourceHeight;
            remainingHeight -= heightToRender;

            if (remainingHeight > 0) {
              // Need another page
              pdf.addPage();
              currentY = margin;
            } else {
              // Done, update currentY
              currentY += heightToRender + spacing;
            }
          }

          return imgHeight;
        }

        // Content fits on one page, but check if we should start fresh
        // If content won't fit with at least 40mm of content visible, start fresh page
        if (imgHeight > remainingSpace - 40) {
          pdf.addPage();
          currentY = margin;
        }
      } else {
        // Normal page break check
        checkPageBreak(imgHeight);
      }

      pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, imgHeight);
      currentY += imgHeight + spacing; // Add spacing

      return imgHeight;
    };

    // Generate header with branding
    await addContentToPDF(createHeaderHTML(brandingData, logoBase64));

    // Generate executive summary
    await addContentToPDF(createExecutiveSummaryHTML(sortedReportData, brandingData));

    // Generate scan information
    await addContentToPDF(createScanInfoHTML(sortedReportData));

    // Generate warning banner for partially completed scans
    if (sortedReportData.warnings && sortedReportData.warnings.length > 0) {
      await addContentToPDF(createPartialCompletionBannerHTML(sortedReportData));
    }

    // Generate vulnerabilities section header
    await addContentToPDF(createVulnHeaderHTML(sortedReportData, brandingData));

    // Generate each vulnerability separately to avoid page breaks
    if (sortedReportData.vulnerabilities.length > 0) {
      for (const vuln of sortedReportData.vulnerabilities) {
        // Use forceNewPageIfTall to handle long recommendations
        await addContentToPDF(createVulnerabilityHTML(vuln), pageWidth, 5, true);
      }
    } else {
      await addContentToPDF(createNoVulnerabilitiesHTML());
    }

    // Add SOC2 compliance section if available (split into parts for better pagination)
    if (sortedReportData.soc2Compliance) {
      const soc2 = sortedReportData.soc2Compliance;

      // Header and overall score
      await addContentToPDF(createSOC2HeaderHTML(soc2));

      // Each technical control separately (with minimal spacing)
      const sortedControls = Object.entries(soc2.technical_controls).sort(
        ([, a], [, b]) => a.score - b.score
      );
      for (const [, control] of sortedControls) {
        await addContentToPDF(createSOC2ControlHTML(control), pageWidth, 1);
      }

      // Organizational controls
      await addContentToPDF(createSOC2OrganizationalHTML(soc2));
    }

    // Add contact information if branding exists and has email or phone
    if (brandingData && (brandingData.contact_info?.primary_email || brandingData.contact_info?.phone)) {
      await addContentToPDF(createContactHTML(brandingData));
    }

    // Generate filename with company name, URL, and timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
    const companyName = brandingData?.contact_info?.company_name
      ? brandingData.contact_info.company_name.replace(/[^a-zA-Z0-9]/g, '_')
      : 'Security';

    // Clean up URL for filename (remove protocol, replace special chars)
    const cleanUrl = reportData.target
      .replace(/^https?:\/\//, '')  // Remove protocol
      .replace(/\/$/, '')            // Remove trailing slash
      .replace(/[^a-zA-Z0-9.-]/g, '_'); // Replace special chars

    const filename = `${companyName}_report_${cleanUrl}_${timestamp}.pdf`;

    // Always download with proper filename (don't open in new tab)
    const pdfBlob = pdf.output('blob');
    const url = URL.createObjectURL(pdfBlob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL after download
    setTimeout(() => URL.revokeObjectURL(url), 1000);

  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF report');
  }
};

// Helper functions for creating individual sections with branding support

const getCSS = () => `
  <style>
    * { box-sizing: border-box; }
    body, html { line-height: 1.5; }
    .bg-white { background-color: #ffffff; }
    .bg-red-100 { background-color: #fef2f2; }
    .bg-yellow-100 { background-color: #fefce8; }
    .bg-blue-100 { background-color: #eff6ff; }
    .bg-gray-100 { background-color: #f3f4f6; }
    .bg-blue-50 { background-color: #eff6ff; }
    .bg-gray-50 { background-color: #f9fafb; }
    .text-red-800 { color: #991b1b; }
    .text-yellow-800 { color: #a16207; }
    .text-blue-800 { color: #1e40af; }
    .text-gray-800 { color: #1f2937; }
    .text-blue-700 { color: #1d4ed8; }
    .text-green-600 { color: #16a34a; }
    .text-yellow-600 { color: #ca8a04; }
    .text-red-600 { color: #dc2626; }
    .text-gray-600 { color: #6b7280; }
    .text-gray-700 { color: #374151; }
    .text-gray-900 { color: #111827; }
    .text-gray-500 { color: #6b7280; }
    .text-green-600 { color: #16a34a; }
    .border { border: 1px solid #e5e7eb; }
    .border-b { border-bottom: 1px solid #e5e7eb; }
    .border-l-4 { border-left: 4px solid #60a5fa; }
    .rounded-lg { border-radius: 0.5rem; }
    .rounded-full { border-radius: 9999px; }
    .rounded { border-radius: 0.25rem; }
    .p-4 { padding: 1rem; }
    .p-6 { padding: 1.5rem; }
    .p-3 { padding: 0.75rem; }
    .px-3 { padding: 0 0.75rem; }
    .py-1 { padding: 0.25rem 0; }
    .py-8 { padding: 2rem 0; }
    .mb-2 { margin-bottom: 0.5rem; }
    .mb-3 { margin-bottom: 0.75rem; }
    .mb-4 { margin-bottom: 1rem; }
    .mb-6 { margin-bottom: 1.5rem; }
    .mt-1 { margin-top: 0.25rem; }
    .mt-4 { margin-top: 1rem; }
    .grid { display: grid; }
    .grid-cols-2 { grid-template-columns: 1fr 1fr; }
    .gap-4 { gap: 1rem; }
    .gap-2 { gap: 0.5rem; }
    .flex { display: flex; }
    .items-center { align-items: center; }
    .items-start { align-items: flex-start; }
    .items-baseline { align-items: baseline; }
    .justify-between { justify-content: space-between; }
    .flex-wrap { flex-wrap: wrap; }
    .text-xl { font-size: 1.25rem; }
    .text-lg { font-size: 1.125rem; }
    .text-2xl { font-size: 1.5rem; }
    .text-sm { font-size: 0.875rem; }
    .text-xs { font-size: 0.75rem; }
    .font-bold { font-weight: 700; }
    .font-semibold { font-weight: 600; }
    .font-medium { font-weight: 500; }
    .inline-flex { display: inline-flex; align-items: center; vertical-align: middle; }
    .text-center { text-align: center; }
    .space-y-4 > * + * { margin-top: 1rem; }
    .space-y-2 > * + * { margin-top: 0.5rem; }
    .max-w-4xl { max-width: 56rem; }
    .mx-auto { margin: 0 auto; }
    .shadow-sm { box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
  </style>
`;

const getSeverityBadgeHTML = (severity: string) => {
  const badgeClasses = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-blue-100 text-blue-800',
    info: 'bg-gray-100 text-gray-800',
    informational: 'bg-gray-100 text-gray-800'
  };

  const className = badgeClasses[severity as keyof typeof badgeClasses] || 'bg-gray-100 text-gray-800';
  return `<span class="inline-flex items-center rounded-full px-2 text-xs font-medium ${className}" style="display: inline-block; vertical-align: middle; height: 18px; line-height: 16px; text-align: center;">${severity.charAt(0).toUpperCase() + severity.slice(1)}</span>`;
};

const getTimeEstimateBadgeHTML = (timeEstimate: string) => {
  return `<span class="inline-flex items-center rounded-full px-2 text-xs font-medium bg-purple-100 text-purple-800" style="display: inline-block; vertical-align: middle; height: 18px; line-height: 16px; text-align: center; margin-left: 6px; white-space: nowrap;">${timeEstimate}</span>`;
};



const createHeaderHTML = (branding?: any, logoBase64?: string | null) => {
  const primaryColor = branding?.color_scheme?.primary_color || '#1e40af';
  const secondaryColor = branding?.color_scheme?.secondary_color || '#3b82f6';

  return `
    ${getCSS()}
    <div class="bg-white">
      ${branding?.contact_info?.company_name ? `
        <div class="mb-6 pb-4" style="border: 2px solid ${primaryColor}; background-color: ${primaryColor}10; padding: 1.5rem; border-radius: 0.5rem;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem;">
            <div style="display: flex; align-items: center; gap: 1rem;">
              ${logoBase64 ? `<img src="${logoBase64}" alt="Logo" style="height: 48px; object-fit: contain;">` : ''}
              <div>
                <h1 style="color: ${primaryColor}; margin: 0; font-size: 1.25rem; font-weight: bold;">${escapeHTML(branding.contact_info.company_name)}</h1>
                ${branding.messaging?.company_tagline ? `<p style="color: #6b7280; margin: 0.25rem 0 0 0; font-size: 0.875rem;">${escapeHTML(branding.messaging.company_tagline)}</p>` : ''}
              </div>
            </div>
            <div style="background-color: ${secondaryColor}; color: white; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500;">Confidential</div>
          </div>
          <p style="color: #6b7280; margin: 0; font-size: 0.875rem;">Security Assessment Report - Generated ${new Date().toLocaleDateString()}</p>
        </div>
      ` : `
        <div class="mb-6 pb-4" style="border: 2px solid ${primaryColor}; padding: 1.5rem; border-radius: 0.5rem;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1rem;">
            <div>
              <h1 style="color: ${primaryColor}; margin: 0; font-size: 1.25rem; font-weight: bold;">Security Assessment Report</h1>
              <p style="color: #6b7280; margin: 0.5rem 0 0 0; font-size: 0.875rem;">Generated ${new Date().toLocaleDateString()}</p>
            </div>
            <div style="background-color: ${secondaryColor}; color: white; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500;">Confidential</div>
          </div>
        </div>
      `}
    </div>
  `;
};

const createExecutiveSummaryHTML = (reportData: ReportData, branding?: any) => {
  const primaryColor = branding?.color_scheme?.primary_color || '#1e40af';
  const secondaryColor = branding?.color_scheme?.secondary_color || '#3b82f6';
  const accentColor = branding?.color_scheme?.success_color || '#60a5fa';
  const severityBreakdown = {
    high: reportData.vulnerabilities.filter(v => v.severity === 'high').length,
    medium: reportData.vulnerabilities.filter(v => v.severity === 'medium').length,
    low: reportData.vulnerabilities.filter(v => v.severity === 'low').length,
    info: reportData.vulnerabilities.filter(v => v.severity === 'info' || v.severity === 'informational').length
  };

  return `
    ${getCSS()}
    <div class="bg-white border rounded-lg shadow-sm">
      <div class="p-6">
        <h2 class="text-xl font-semibold mb-4" style="color: ${primaryColor};">Executive Summary</h2>
        <div class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <h4 class="font-medium text-gray-700">Security Score</h4>
              <p class="text-2xl font-bold" style="color: ${accentColor};">
                ${reportData.securityScore}/100
              </p>
            </div>
            <div>
              <h4 class="font-medium text-gray-700">Total Issues</h4>
              <p class="text-2xl font-bold" style="color: ${secondaryColor};">
                ${reportData.vulnerabilities.length}
              </p>
            </div>
          </div>
          <div class="space-y-2">
            <h4 class="font-medium" style="color: ${primaryColor};">Severity Breakdown</h4>
            <div class="flex gap-4" style="align-items: center;">
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                ${getSeverityBadgeHTML('high')}
                <span class="text-sm" style="height: 18px; line-height: 16px; display: inline-block; margin-top: -1px;">${severityBreakdown.high}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                ${getSeverityBadgeHTML('medium')}
                <span class="text-sm" style="height: 18px; line-height: 16px; display: inline-block; margin-top: -1px;">${severityBreakdown.medium}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                ${getSeverityBadgeHTML('low')}
                <span class="text-sm" style="height: 18px; line-height: 16px; display: inline-block; margin-top: -1px;">${severityBreakdown.low}</span>
              </div>
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                ${getSeverityBadgeHTML('info')}
                <span class="text-sm" style="height: 18px; line-height: 16px; display: inline-block; margin-top: -1px;">${severityBreakdown.info}</span>
              </div>
            </div>
          </div>
          ${reportData.vulnerabilities.length > 0 ? `
            <div style="background-color: #eff6ff; border-left: 4px solid #3b82f6; border-radius: 8px; padding: 12px 16px; margin-top: 16px;">
              <p style="color: #1e40af; font-size: 14px; line-height: 1.5; margin: 0;">
                After fixing these ${reportData.vulnerabilities.length} issue${reportData.vulnerabilities.length !== 1 ? 's' : ''}, re-scan to verify fixes and track your security score improvement.
              </p>
            </div>
          ` : ''}
        </div>
      </div>
    </div>
  `;
};

const createScanInfoHTML = (reportData: ReportData) => `
  ${getCSS()}
  <div class="bg-white border rounded-lg shadow-sm">
    <div class="p-6">
      <h2 class="text-xl font-semibold text-gray-900 mb-4">Scan Information</h2>
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p class="text-gray-600">Scan Type:</p>
          <p class="font-medium">${reportData.scanType}</p>
        </div>
        <div>
          <p class="text-gray-600">Duration:</p>
          <p class="font-medium">${reportData.scanDetails.duration}</p>
        </div>
        <div>
          <p class="text-gray-600">Start Time:</p>
          <p class="font-medium">${new Date(reportData.scanDetails.startTime).toLocaleString()}</p>
        </div>
        <div>
          <p class="text-gray-600">End Time:</p>
          <p class="font-medium">${new Date(reportData.scanDetails.endTime).toLocaleString()}</p>
        </div>
      </div>
      <div class="mt-4">
        <p class="text-gray-600 text-sm">Tools Used:</p>
        <div class="flex flex-wrap gap-2 mt-1">
          ${reportData.scanDetails.toolsUsed.map(tool =>
            `<span class="border rounded-full px-3 py-1 text-xs font-medium text-gray-800">${tool}</span>`
          ).join('')}
        </div>
      </div>
      ${reportData.scan_completeness ? `
        <div class="mt-4">
          <p class="text-gray-600 text-sm">Scan Completeness:</p>
          <p class="font-medium text-sm mt-1">${reportData.scan_completeness}</p>
        </div>
      ` : ''}
    </div>
  </div>
`;

const createPartialCompletionBannerHTML = (reportData: ReportData) => {
  const warningCount = reportData.warnings?.length || 0;
  const failedTools = reportData.warnings?.map(w => w.tool).join(', ') || '';

  return `
    ${getCSS()}
    <div class="bg-white border rounded-lg shadow-sm" style="border-color: #fbbf24; background-color: #fffbeb;">
      <div class="p-6">
        <div style="display: flex; align-items: start; gap: 1rem;">
          <div style="background-color: #fbbf24; color: white; border-radius: 50%; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-weight: bold; font-size: 18px;">!</div>
          <div style="flex: 1;">
            <h3 class="font-semibold" style="color: #92400e; font-size: 16px; margin: 0 0 8px 0;">Partially Completed Scan</h3>
            <p class="text-sm" style="color: #78350f; margin: 0 0 12px 0; line-height: 1.5;">
              ${warningCount} tool${warningCount !== 1 ? 's' : ''} failed to complete during this scan. The security assessment results shown below are based on the tools that completed successfully.
            </p>
            <div class="space-y-2" style="margin-top: 12px;">
              ${reportData.warnings?.map(warning => `
                <div class="bg-white p-3 rounded border" style="background-color: white; border: 1px solid #fbbf24; border-radius: 6px; padding: 12px;">
                  <div style="display: flex; align-items: start; gap: 8px;">
                    <span class="font-semibold text-sm" style="color: #92400e; font-weight: 600;">
                      ${warning.tool.toUpperCase()}
                    </span>
                    <span class="text-xs px-2 rounded" style="background-color: ${warning.severity === 'high' ? '#fef2f2' : warning.severity === 'medium' ? '#fef9c3' : '#f0f9ff'}; color: ${warning.severity === 'high' ? '#991b1b' : warning.severity === 'medium' ? '#854d0e' : '#1e40af'}; padding: 2px 8px; border-radius: 4px;">
                      ${warning.type}
                    </span>
                  </div>
                  <p class="text-sm" style="color: #78350f; margin: 8px 0 0 0; font-size: 13px; line-height: 1.4;">
                    ${warning.message}
                  </p>
                  ${warning.impact ? `
                    <div class="mt-2 text-sm" style="margin-top: 8px;">
                      <span style="color: #92400e; font-weight: 600; font-size: 12px;">Impact:</span>
                      <span style="color: #78350f; font-size: 12px;"> ${warning.impact}</span>
                    </div>
                  ` : ''}
                  ${warning.recommendation ? `
                    <div class="mt-2 p-2 rounded" style="margin-top: 8px; background-color: #fffbeb; border-left: 3px solid #fbbf24; padding: 8px 12px;">
                      <p class="text-sm" style="color: #78350f; margin: 0; font-size: 12px; line-height: 1.4;">
                        <strong style="font-weight: 600;">Recommendation:</strong> ${warning.recommendation}
                      </p>
                    </div>
                  ` : ''}
                </div>
              `).join('')}
            </div>
            <div class="mt-4 p-3 rounded" style="margin-top: 16px; background-color: #dbeafe; border-left: 4px solid #3b82f6; padding: 12px;">
              <p class="text-sm" style="color: #1e40af; margin: 0; line-height: 1.5; font-size: 13px;">
                <strong style="font-weight: 600;">Note:</strong> For a complete security assessment, we recommend re-running this scan or running standalone scans for the failed tools: ${failedTools}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

const createVulnHeaderHTML = (reportData: ReportData, branding?: any) => {
  const primaryColor = branding?.color_scheme?.primary_color || '#1e40af';
  const secondaryColor = branding?.color_scheme?.secondary_color || '#3b82f6';
  const issueCount = reportData.vulnerabilities.length;

  return `
    ${getCSS()}
    <div class="bg-white border rounded-lg shadow-sm">
      <div class="p-3">
        <div style="display: flex; align-items: center; gap: 0.75rem;">
          <h2 class="text-lg font-semibold" style="color: ${primaryColor};">Security Issues</h2>
          <span style="background-color: ${secondaryColor}; color: white; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 500;">
            ${issueCount} issue${issueCount !== 1 ? 's' : ''} found
          </span>
        </div>
      </div>
    </div>
  `;
};


// Convert markdown recommendation to HTML
const markdownToHTML = (markdown: string): string => {
  if (!markdown) return '';

  let html = markdown;

  // Convert code blocks with language specifier (```language)
  html = html.replace(/```(\w+)\n([\s\S]*?)```/g, (match, lang, code) => {
    let processedCode = code.trim();

    // Add basic syntax highlighting BEFORE escaping HTML
    // Strings (single and double quoted) - mark with placeholder
    const stringPlaceholders: string[] = [];
    processedCode = processedCode.replace(/(['"])((?:(?!\1).)*)\1/g, (_match: string, quote: string, content: string) => {
      const placeholder = `__STRING_${stringPlaceholders.length}__`;
      stringPlaceholders.push(`<span style="color: #ce9178;">${quote}${content}${quote}</span>`);
      return placeholder;
    });

    // Comments (# and //) - mark with placeholder
    const commentPlaceholders: string[] = [];
    processedCode = processedCode.replace(/(#|\/\/)(.*)$/gm, (match: string) => {
      const placeholder = `__COMMENT_${commentPlaceholders.length}__`;
      commentPlaceholders.push(`<span style="color: #6a9955;">${match}</span>`);
      return placeholder;
    });

    // Keywords (common ones) - mark with placeholder
    const keywordPlaceholders: string[] = [];
    processedCode = processedCode.replace(/\b(add_header|Header|app\.use|location|server|if|function|const|let|var|return|require|export|import)\b/g, (match: string) => {
      const placeholder = `__KEYWORD_${keywordPlaceholders.length}__`;
      keywordPlaceholders.push(`<span style="color: #569cd6;">${match}</span>`);
      return placeholder;
    });

    // Numbers - mark with placeholder
    const numberPlaceholders: string[] = [];
    processedCode = processedCode.replace(/\b(\d+)\b/g, (match: string) => {
      const placeholder = `__NUMBER_${numberPlaceholders.length}__`;
      numberPlaceholders.push(`<span style="color: #b5cea8;">${match}</span>`);
      return placeholder;
    });

    // Now escape HTML entities
    processedCode = processedCode
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    // Restore placeholders with their styled versions
    stringPlaceholders.forEach((replacement, i) => {
      processedCode = processedCode.replace(`__STRING_${i}__`, replacement);
    });
    commentPlaceholders.forEach((replacement, i) => {
      processedCode = processedCode.replace(`__COMMENT_${i}__`, replacement);
    });
    keywordPlaceholders.forEach((replacement, i) => {
      processedCode = processedCode.replace(`__KEYWORD_${i}__`, replacement);
    });
    numberPlaceholders.forEach((replacement, i) => {
      processedCode = processedCode.replace(`__NUMBER_${i}__`, replacement);
    });

    return `<div style="background-color: #1e1e1e; color: #d4d4d4; padding: 14px; border-radius: 6px; margin: 10px 0; font-family: 'Courier New', Consolas, Monaco, monospace; font-size: 10px; white-space: pre-wrap; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.5; border: 1px solid #333;"><div style="color: #608b4e; font-size: 9px; margin-bottom: 8px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px;">${lang}</div>${processedCode}</div>`;
  });

  // Convert inline code (`code`)
  html = html.replace(/`([^`]+)`/g, '<code style="background-color: #f1f5f9; padding: 2px 6px; border-radius: 3px; font-family: \'Courier New\', monospace; font-size: 11px; color: #334155;">$1</code>');

  // Convert bold (**text**)
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-weight: 600;">$1</strong>');

  // Convert links ([text](url))
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline; font-weight: 500;">$1</a>');

  // Convert line breaks
  html = html.replace(/\n\n/g, '</p><p style="margin: 8px 0; line-height: 1.5;">');
  html = html.replace(/\n/g, '<br>');

  // Wrap in paragraph if not already wrapped
  if (!html.startsWith('<div') && !html.startsWith('<p>')) {
    html = `<p style="margin: 0; line-height: 1.5;">${html}</p>`;
  }

  return html;
};

const createVulnerabilityHTML = (vuln: any) => {
  // Parse evidence if it's a JSON string
  let evidenceData = vuln.evidence;
  if (typeof vuln.evidence === 'string') {
    try {
      evidenceData = JSON.parse(vuln.evidence);
    } catch (e) {
      // If it's not JSON, keep as string
      evidenceData = vuln.evidence;
    }
  }

  // Check if evidence is an array (multiple instances)
  const hasInstances = Array.isArray(evidenceData) && evidenceData.length > 0;
  const instanceCount = hasInstances ? evidenceData.length : 0;

  // Limit instances to prevent huge PDFs (show first 5, then summary)
  const maxInstances = 5;
  const instancesToShow = hasInstances ? evidenceData.slice(0, maxInstances) : [];
  const hasMoreInstances = instanceCount > maxInstances;

  return `
    ${getCSS()}
    <div class="bg-white border rounded-lg shadow-sm">
      <div class="p-4">
        <div class="flex items-start justify-between mb-3">
          <h4 class="font-semibold text-gray-900">${escapeHTML(vuln.title)}</h4>
          <div>
            ${getSeverityBadgeHTML(vuln.severity)}${vuln.remediationTime ? getTimeEstimateBadgeHTML(vuln.remediationTime) : ''}
          </div>
        </div>
        <p class="text-gray-700 mb-3 text-sm">${escapeHTML(vuln.description)}</p>

        ${hasInstances ? `
          <div class="mb-3">
            <div class="text-sm font-medium text-gray-700 mb-2">
              ${instanceCount} instance${instanceCount > 1 ? 's' : ''} found${hasMoreInstances ? ` (showing first ${maxInstances})` : ''}:
            </div>
            <div class="space-y-2 pl-3 border-l-2" style="border-color: #e5e7eb;">
              ${instancesToShow.map((instance: any, index: number) => `
                <div class="bg-gray-50 p-3 rounded text-sm" style="background-color: #f9fafb;">
                  <div class="font-medium text-gray-700 mb-1">
                    ${escapeHTML(instance.location || `Instance ${index + 1}`)}
                  </div>
                  ${instance.description ? `
                    <div class="text-gray-600 mb-1">${escapeHTML(instance.description)}</div>
                  ` : ''}
                  ${instance.evidence ? `
                    <div class="font-mono text-xs bg-white p-2 rounded border" style="background-color: white; border: 1px solid #e5e7eb;">
                      ${escapeHTML(instance.evidence)}
                    </div>
                  ` : ''}
                </div>
              `).join('')}
              ${hasMoreInstances ? `
                <div class="text-gray-500 text-sm text-center py-2 italic">
                  ... and ${instanceCount - maxInstances} more instances
                </div>
              ` : ''}
            </div>
          </div>
        ` : evidenceData && typeof evidenceData === 'string' ? `
          <div class="mb-3">
            <div class="text-sm font-medium text-gray-700 mb-1">Evidence:</div>
            <div class="font-mono text-xs bg-gray-50 p-2 rounded border" style="background-color: #f9fafb; border: 1px solid #e5e7eb;">
              ${escapeHTML(evidenceData)}
            </div>
          </div>
        ` : ''}

        <div class="bg-blue-50 p-3 rounded border-l-4" style="background-color: #eff6ff; border-left: 4px solid #3b82f6;">
          <h5 class="font-medium text-blue-800 mb-2" style="color: #1e40af; font-weight: 600; font-size: 13px; margin-bottom: 8px;">Recommendation:</h5>
          <div class="text-blue-700 text-sm" style="color: #1e40af; font-size: 12px;">
            ${markdownToHTML(vuln.recommendation)}
          </div>
        </div>
      </div>
    </div>
  `;
};

const createNoVulnerabilitiesHTML = () => `
  ${getCSS()}
  <div class="bg-white border rounded-lg shadow-sm">
    <div class="p-6 text-center py-8">
      <h3 class="text-lg font-medium text-green-600">No Issues Found</h3>
      <p class="text-gray-500">This scan found no security vulnerabilities.</p>
    </div>
  </div>
`;

const createContactHTML = (branding: any) => {
  const primaryColor = branding?.color_scheme?.primary_color || '#1e40af';
  const secondaryColor = branding?.color_scheme?.secondary_color || '#3b82f6';

  return `
    ${getCSS()}
    <div class="bg-white border rounded-lg shadow-sm" style="border-color: ${primaryColor};">
      <div class="p-6" style="background: linear-gradient(135deg, ${primaryColor}08 0%, ${secondaryColor}08 100%);">
        <h2 class="text-xl font-semibold mb-4" style="color: ${primaryColor};">Contact Information</h2>
        <div class="grid grid-cols-2 gap-4 text-sm">
          ${branding.contact_info?.primary_email ? `
            <div>
              <p class="text-gray-600">Email:</p>
              <p class="font-medium" style="color: ${secondaryColor};">${escapeHTML(branding.contact_info.primary_email)}</p>
            </div>
          ` : ''}
          ${branding.contact_info?.phone ? `
            <div>
              <p class="text-gray-600">Phone:</p>
              <p class="font-medium" style="color: ${secondaryColor};">${escapeHTML(branding.contact_info.phone)}</p>
            </div>
          ` : ''}
          ${branding.contact_info?.website ? `
            <div>
              <p class="text-gray-600">Website:</p>
              <p class="font-medium" style="color: ${secondaryColor};">${escapeHTML(branding.contact_info.website)}</p>
            </div>
          ` : ''}
          ${branding.contact_info?.address ? `
            <div>
              <p class="text-gray-600">Address:</p>
              <p class="font-medium" style="color: ${secondaryColor};">${escapeHTML(branding.contact_info.address)}</p>
            </div>
          ` : ''}
        </div>
      </div>
    </div>
  `;
};
